import { useEffect, useRef, useState, type SetStateAction } from "react";
import {
  addLine,
  canAddLine,
  canRemoveLine,
  getTotals,
  nextLocalLineKey,
  removeLine,
  resolveLine,
  selectOnLine,
  setLineQuantity,
  type CheckoutLine,
  type CheckoutLink,
} from "@/lib/express-cart/checkout";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/Button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ProductGroup } from "./ProductGroup";
import type { ProductVerificationResult } from "@/lib/product-verification";
import { groupMarketplaceProducts } from "@/lib/express-cart/marketplace-groups";

/**
 * Where the cart's lines live and how "Continue" hands them off; see
 * hooks/useCartSession.ts (used for brand links, /cart/:slug, and the agent's
 * View Cart link, /cart).
 */
export interface ExpressCartController {
  lines: CheckoutLine[];
  cartSaveFailures: Array<{
    productId: string;
    variant: string | null;
    message: string;
  }>;
  setLines: (update: SetStateAction<CheckoutLine[]>) => void;
  /** True while saved lines are being loaded; the cart isn't editable yet. */
  isRestoring: boolean;
  /** Navigates away on success; throws an Error with a shopper-facing message otherwise. */
  continueToCheckout: () => Promise<void>;
  verification: ProductVerificationResult | null;
  isVerifying: boolean;
  isVerificationPending: boolean;
  requiresVerification: boolean;
  hasSession: boolean;
  liveStockBySku: Map<string, number>;
  livePricesBySku: Map<string, number>;
}

interface Props {
  link: CheckoutLink;
  controller: ExpressCartController;
  integrationType: string | null;
}

interface MergeNotice {
  productId: string;
  lineKey: string;
  message: string;
}

export function ExpressCart({ link, controller, integrationType }: Props) {
  const {
    cartSaveFailures,
    lines,
    setLines,
    isRestoring,
    continueToCheckout,
    verification,
    isVerifying,
    isVerificationPending,
    requiresVerification,
    hasSession,
    liveStockBySku,
    livePricesBySku,
  } = controller;
  const [attempted, setAttempted] = useState(false);
  const [merge, setMerge] = useState<MergeNotice | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const nextLineKeyRef = useRef(0);

  useEffect(() => {
    if (!merge) return;
    const timer = setTimeout(() => setMerge(null), 4000);
    return () => clearTimeout(timer);
  }, [merge]);

  const totals = getTotals(link, lines);
  const hasVerifiedStockIssues = Boolean(verification?.issues.length);
  const saveFailureByRow = new Map(
    cartSaveFailures.map((failure) => [
      `${failure.productId}|${failure.variant ?? ""}`,
      failure.message,
    ]),
  );
  const displaySubtotalCents = lines.reduce((subtotal, line) => {
    const product = link.products.find((candidate) => candidate.id === line.productId);
    if (!product) return subtotal;
    const resolved = resolveLine(product, line);
    if (resolved.inactive) return subtotal;
    const sku = resolved.variant?.sku ?? resolved.product.sku;
    const livePrice = sku
      ? livePricesBySku.get(`${product.id}:${sku}`)
      : undefined;
    return subtotal + (livePrice ?? resolved.unitPriceCents) * line.quantity;
  }, 0);

  function handleSelect(clientKey: string, axisName: string, value: string) {
    const result = selectOnLine(link, lines, clientKey, axisName, value);
    setLines(result.lines);
    if (result.mergedInto) {
      const merged = result.lines.find((l) => l.clientKey === result.mergedInto);
      const product = merged && link.products.find((p) => p.id === merged.productId);
      const title = product && merged ? resolveLine(product, merged).variant?.title : undefined;
      if (merged) {
        setMerge({
          productId: merged.productId,
          lineKey: merged.clientKey,
          message: `${title ?? "That option"} was already in your cart, so we combined them.`,
        });
      }
    }
  }

  function handleAdd(productId: string) {
    const clientKey = nextLocalLineKey(nextLineKeyRef);
    setLines((current) => addLine(current, productId, clientKey));
  }

  async function handleContinue() {
    setAttempted(true);
    setCheckoutError(null);
    if (
      !totals.canContinue ||
      hasVerifiedStockIssues ||
      cartSaveFailures.length > 0 ||
      isPending ||
      isVerifying ||
      (requiresVerification && hasSession && isVerificationPending)
    ) return;

    setIsPending(true);
    try {
      // Resolves only after navigation has started; the page is leaving.
      await continueToCheckout();
    } catch (error) {
      setIsPending(false);
      setCheckoutError(
        error instanceof Error && error.message
          ? error.message
          : "We couldn't start checkout. Check your connection and try again.",
      );
    }
  }

  const stockIssueProductIds = new Set([
    ...lines.flatMap((line) => {
      const product = link.products.find((candidate) => candidate.id === line.productId);
      return product && !resolveLine(product, line).inStock ? [line.productId] : [];
    }),
    ...(verification?.issues.map(({ item }) => item.product_id) ?? []),
  ]);
  const blockers = [
    ...(totals.itemCount === 0 ? ["Add at least one item"] : []),
    ...Array.from(new Set(lines.flatMap((line) => {
      if (line.quantity === 0 || stockIssueProductIds.has(line.productId)) return [];
      const product = link.products.find((candidate) => candidate.id === line.productId);
      return product && resolveLine(product, line).needsVariant ? [product.title] : [];
    }))).map((title) => `Choose options for ${title}`),
    ...totals.outOfStockProducts.map((title) => `${title} is out of stock`),
    ...(verification?.issues.map(({ item, stock }) =>
      stock > 0 ? `Only ${stock} of ${item.title} are available` : `${item.title} is out of stock`,
    ) ?? []),
    ...cartSaveFailures.map((failure) => {
      const product = link.products.find((candidate) => candidate.id === failure.productId);
      return `${product?.title ?? "An item"}: adjust the quantity or remove this item to continue`;
    }),
  ];

  const renderProduct = (product: CheckoutLink["products"][number]) => {
    const rows = lines
      .filter((line) => line.productId === product.id)
      .map((line) => ({
        line,
        resolved: resolveLine(product, line),
        removable: canRemoveLine(lines, line.clientKey),
      }));
    // A removed cart item that isn't one of the link's has no line left.
    if (rows.length === 0) return null;
    return (
      <ProductGroup
        key={product.id}
        product={product}
        rows={rows}
        dimInactive={!link.code}
        canAdd={canAddLine(link, lines, product.id)}
        notice={merge?.productId === product.id ? merge.message : null}
        highlightLineKey={merge?.productId === product.id ? merge.lineKey : null}
        onSelect={handleSelect}
        onQuantityChange={(clientKey, quantity) => setLines((current) => setLineQuantity(link, current, clientKey, quantity))}
        onRemove={(clientKey) => setLines((current) => removeLine(current, clientKey))}
        onAdd={() => handleAdd(product.id)}
        stockBySku={liveStockBySku}
        priceBySku={livePricesBySku}
        saveFailureByRow={saveFailureByRow}
        showStockMessages={
          !requiresVerification ||
          Boolean(verification && verification.failedRetailerIds.length === 0)
        }
      />
    );
  };

  // Not `disabled` when blocked: a tap shows the shopper what's missing.
  const continueButton = (fullWidth: boolean) => (
    <Button
      onClick={handleContinue}
      isLoading={isPending || isVerifying}
      disabled={
        hasVerifiedStockIssues ||
        cartSaveFailures.length > 0 ||
        isPending ||
        (requiresVerification && hasSession && isVerificationPending)
      }
      size="lg"
      fullWidth={fullWidth}
      className={totals.canContinue ? undefined : "opacity-60"}
    >
      Continue to checkout
    </Button>
  );

  if (isRestoring) {
    return (
      <main className="mx-auto flex max-w-5xl justify-center px-4 py-24">
        <LoadingSpinner size="lg" />
      </main>
    );
  }

  return (
    <>
      <main className="mx-auto grid max-w-5xl gap-6 px-4 py-6 lg:grid-cols-[1fr_340px]">
        <section aria-labelledby="cart-heading" className="rounded-2xl bg-(--color-surface-primary) p-5 shadow-sm">
          <h1 id="cart-heading" className="text-xl font-bold">
            Your cart
          </h1>
          {link.name && <p className="text-sm text-(--color-text-muted)">{link.name}</p>}
          {link.retailer_id === "829a63b7-3324-4b53-8a51-c1e4b165e08a" && (
            <>
              <p className="mt-4 rounded-lg bg-[#e4e020] p-3 text-sm text-black">
                Products are made to order. Processing takes 5 to 10 days, plus shipping.
              </p>
              <p className="mt-4 ml-3 text-sm font-bold">
                Please select your fabric from the options below
              </p>
            </>
          )}

          <ul className="divide-y divide-(--color-surface-border-light)">
            {integrationType === "marketplace"
              ? groupMarketplaceProducts(link, lines).flatMap((group) => [
                  <li key={`brand:${group.id}`} className="pb-2 pt-5 first:pt-0">
                    <h2 className="text-sm font-semibold text-(--color-text-secondary)">{group.name}</h2>
                  </li>,
                  ...group.products.map(renderProduct),
                ])
              : link.products.map(renderProduct)}
          </ul>
        </section>

        <aside aria-labelledby="summary-heading" className="h-fit rounded-2xl bg-(--color-surface-primary) p-5 shadow-sm lg:sticky lg:top-6">
          <h2 id="summary-heading" className="mb-4 text-lg font-bold">
            Order summary
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-(--color-text-secondary)">Subtotal ({totals.itemCount} items)</dt>
              <dd className="tabular-nums">{formatCurrency(displaySubtotalCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-(--color-text-secondary)">Shipping</dt>
              <dd className="text-(--color-text-muted)">Calculated next</dd>
            </div>
            <div className="flex justify-between border-t border-(--color-surface-border) pt-3 text-base font-bold">
              <dt>Total</dt>
              <dd className="tabular-nums">{formatCurrency(displaySubtotalCents)}</dd>
            </div>
          </dl>

          {(attempted && blockers.length > 0 || hasVerifiedStockIssues) && (
            <div role="status" className="mt-4 rounded-lg bg-(--color-surface-secondary) p-3 text-sm">
              <ul className="list-inside list-disc text-(--color-error)">
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
          {verification && verification.failedRetailerIds.length > 0 && (
            <p role="status" className="mt-4 text-sm text-(--color-warning)">
              We couldn't confirm live stock for some items. You can continue, but stock will be checked before payment.
            </p>
          )}
          {checkoutError && (
            <p role="alert" className="mt-4 rounded-lg bg-(--color-error-bg) p-3 text-sm text-(--color-error)">
              {checkoutError}
            </p>
          )}

          <div className="mt-5 hidden lg:block">{continueButton(true)}</div>
        </aside>
      </main>

      {/* Mobile sticky checkout bar: one centred button (the total is in the order summary) */}
      <div className="fixed inset-x-0 bottom-0 border-t border-(--color-surface-border) bg-(--color-surface-primary) px-4 py-3 lg:hidden">
        {(checkoutError || (attempted && blockers.length > 0) || hasVerifiedStockIssues) && (
          <p className="mx-auto mb-2 max-w-sm text-center text-xs text-(--color-error)" aria-hidden>
            {checkoutError ?? blockers[0] ?? "Update your cart to continue"}
          </p>
        )}
        <div className="mx-auto w-full max-w-sm">{continueButton(true)}</div>
      </div>
    </>
  );
}
